/**
 * Zajednička obrada grešaka koje backend vraća kroz ApiResponse format
 * (ili ProblemDetails za greške iz middleware-a).
 *
 * Backend koristi dva koda sa HTTP 409:
 *  - `DeviceLimitReached` — dostignut limit mobilnih uređaja;
 *  - `ConcurrencyConflict` — zapis je izmenjen između čitanja i upisa
 *    (optimistička konkurentnost / `expectedBalance`).
 *
 * Poruke moraju biti jasne korisniku, a ne generička greška.
 */

export const DEVICE_LIMIT_REACHED_CODE = 'DeviceLimitReached';

export const DEVICE_LIMIT_REACHED_MESSAGE =
  'Dostigli ste maksimalan broj dozvoljenih uređaja.\n\nOdjavite jedan od postojećih uređaja i pokušajte ponovo.';

export const CONCURRENCY_CONFLICT_CODE = 'ConcurrencyConflict';

export const CONCURRENCY_CONFLICT_TITLE = 'Podaci su u međuvremenu izmenjeni';

export const CONCURRENCY_CONFLICT_MESSAGE =
  'Neko drugi je izmenio ovaj zapis dok ste ga uređivali. Osvežite podatke i pokušajte ponovo.';

// Poruke/kodovi koje backend može poslati unutar ApiResponse omotača.
const MESSAGE_HINTS = [
  'devicelimitreached',
  'device-limit-reached',
  'device limit',
  'maksimalan broj dozvoljenih uređaja',
  'maksimalan broj uređaja',
];

// Prepoznavanje optimističke konkurentnosti. Namerno se NE koristi samo HTTP 409 —
// 409 koristi i `DeviceLimitReached`, pa bi se limit uređaja prikazao kao konflikt.
const CONCURRENCY_HINTS = [
  'concurrencyconflict',
  'izmenjeni u međuvremenu',
  'izmenjeno u medjuvremenu',
  'izmenjen u medjuvremenu',
];

/**
 * Sakupi sve tekstualne delove ApiResponse greške (code/message/errors/...).
 * @param {*} data - Response body
 * @returns {string} Lowercase tekst za prepoznavanje koda greške
 */
const collectErrorText = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data.toLowerCase();

  const parts = ['code', 'errorCode', 'error', 'message', 'title', 'detail', 'details']
    .map((field) => data[field]);

  if (Array.isArray(data.errors)) {
    data.errors.forEach((item) => {
      if (typeof item === 'string') {
        parts.push(item);
      } else if (item && typeof item === 'object') {
        parts.push(item.code, item.message);
      }
    });
  } else if (typeof data.errors === 'string') {
    parts.push(data.errors);
  }

  return parts
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ')
    .toLowerCase();
};

/**
 * Da li je odgovor backend-a `ConcurrencyConflict` (zapis je izmenjen u međuvremenu)?
 *
 * @param {number|undefined} status - HTTP status
 * @param {*} data - Response body
 * @returns {boolean}
 */
export const isConcurrencyConflictPayload = (status, data) => {
  const text = collectErrorText(data);
  return CONCURRENCY_HINTS.some((hint) => text.includes(hint));
};

/**
 * Prepoznaje `ConcurrencyConflict` u bilo kom obliku (axios greška ili već
 * transformisana greška sa `code` poljem).
 *
 * @param {*} error - Error/axios error
 * @returns {boolean}
 */
export const isConcurrencyConflictError = (error) => {
  if (!error) return false;
  if (error.code === CONCURRENCY_CONFLICT_CODE) return true;
  return isConcurrencyConflictPayload(error.response?.status, error.response?.data);
};

/**
 * Da li je odgovor backend-a greška `DeviceLimitReached`?
 *
 * Prepoznaje se po kodu/poruci u telu odgovora, uz HTTP 409 kao fallback za
 * starije verzije backend-a. Konflikt se eksplicitno isključuje.
 *
 * @param {number|undefined} status - HTTP status
 * @param {*} data - Response body
 * @returns {boolean}
 */
export const isDeviceLimitReachedPayload = (status, data) => {
  if (isConcurrencyConflictPayload(status, data)) return false;

  const text = collectErrorText(data);
  if (MESSAGE_HINTS.some((hint) => text.includes(hint))) return true;

  return status === 409;
};

/**
 * Izdvaja korisniku čitljivu poruku iz ApiResponse ili ProblemDetails odgovora.
 *
 * ProblemDetails (greške iz middleware-a: 400/409/500) nema `message`, već
 * `title`/`detail` — bez ovoga bi UI prikazivao generičku poruku.
 *
 * @param {*} error - Error/axios error
 * @param {string} fallback - Poruka kada telo odgovora ne sadrži ništa korisno
 * @returns {string}
 */
export const getApiErrorMessage = (error, fallback = 'Došlo je do greške. Pokušajte ponovo.') => {
  if (!error) return fallback;

  const data = error.response?.data;

  if (typeof data === 'string' && data.trim()) return data;

  if (data) {
    // Konflikt ima prioritet: poruka mora da kaže šta korisnik treba da uradi.
    if (isConcurrencyConflictPayload(error.response?.status, data)) {
      return data.message || data.detail || CONCURRENCY_CONFLICT_MESSAGE;
    }

    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
    if (typeof data.title === 'string' && data.title.trim()) return data.title;

    if (Array.isArray(data.errors)) {
      const first = data.errors.find((message) => typeof message === 'string' && message.trim());
      if (first) return first;
    } else if (data.errors && typeof data.errors === 'object') {
      const first = Object.values(data.errors)
        .flat()
        .find((message) => typeof message === 'string' && message.trim());
      if (first) return first;
    }
  }

  if (!error.response && error.message) return error.message;

  return fallback;
};

/**
 * Greška koja se baca kada je dostignut limit uređaja. Ima stabilan `code`
 * kako bi UI (npr. ekran aktivnih uređaja) mogao da je prepozna bez parsiranja
 * poruke.
 */
export class DeviceLimitReachedError extends Error {
  constructor(message = DEVICE_LIMIT_REACHED_MESSAGE, options = {}) {
    super(message);
    this.name = 'DeviceLimitReachedError';
    this.code = DEVICE_LIMIT_REACHED_CODE;
    if (options.cause) {
      this.cause = options.cause;
    }
  }
}

/**
 * Prepoznaje `DeviceLimitReached` grešku u bilo kom obliku (naša klasa ili
 * originalna axios greška).
 * @param {*} error - Error/axios error
 * @returns {boolean}
 */
export const isDeviceLimitReachedError = (error) => {
  if (!error) return false;
  if (error.code === DEVICE_LIMIT_REACHED_CODE || error.name === 'DeviceLimitReachedError') {
    return true;
  }
  return isDeviceLimitReachedPayload(error.response?.status, error.response?.data);
};
